<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("admin-followup.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("admin-followup.error") }}</div>
    <template v-else>
      <!-- header: title + breadcrumb + quick search -->
      <div class="row between" style="margin-bottom: 14px; gap: 12px">
        <div>
          <div class="pagetitle">{{ $t("admin-followup.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("admin-followup.crumb") }}</div>
        </div>
        <div class="searchbar grow" style="max-width: 320px">
          <span>🔎</span>
          <input
            v-model="q"
            type="search"
            data-test="quick-search"
            :placeholder="$t('admin-followup.searchPlaceholder')"
          />
        </div>
      </div>

      <div class="grid2">
        <!-- 1) HR action form -->
        <div class="panel">
          <div class="panel__head"><h4>⚙️ {{ $t("admin-followup.actionCenter") }}</h4></div>
          <div class="panel__body">
            <div class="form one">
              <div class="field">
                <label>{{ $t("admin-followup.form.employee") }} <span class="req">*</span></label>
                <select v-model="form.employeeId" class="select">
                  <option value="">{{ $t("admin-followup.form.employeePlaceholder") }}</option>
                  <option v-for="e in employees" :key="e.id" :value="e.id">
                    {{ e.fullName }} — {{ e.cardNumber }}
                  </option>
                </select>
              </div>
              <div class="field">
                <label>{{ $t("admin-followup.form.action") }} <span class="req">*</span></label>
                <select v-model="form.action" class="select">
                  <option v-for="a in actionKeys" :key="a" :value="a">
                    {{ $t("admin-followup.actions." + a) }}
                  </option>
                </select>
              </div>
              <div v-if="dateVisible" class="field">
                <label>{{ $t("admin-followup.form.lastWorkDay") }} <span class="req">*</span></label>
                <input v-model="form.lastWorkDay" class="input" type="date" />
                <div v-if="form.action === 'leave'" class="hint">{{ $t("admin-followup.form.coverageEndsHint") }}</div>
              </div>
              <div class="field">
                <label>{{ $t("admin-followup.form.note") }}</label>
                <textarea
                  v-model="form.note"
                  class="textarea"
                  rows="2"
                  :placeholder="$t('admin-followup.form.notePlaceholder')"
                ></textarea>
              </div>
            </div>

            <div v-if="errorMsg" class="note" style="border-color: var(--v-danger)">
              <b>!</b>{{ errorMsg }}
            </div>
            <div v-if="successMsg" class="note" style="border-color: var(--v-ok)">
              <b>✓</b>{{ successMsg }}
            </div>

            <div class="divider"></div>
            <div class="row" style="gap: 10px">
              <button class="btn danger" type="button" :disabled="saving" @click="submitAction">
                ⏹ {{ saving ? $t("admin-followup.form.saving") : $t("admin-followup.form.execute") }}
              </button>
              <button class="btn ghost" type="button" :disabled="saving" @click="resetForm">
                {{ $t("admin-followup.form.cancel") }}
              </button>
            </div>
          </div>
        </div>

        <!-- 2) quick status toggle + 3) month summary KPI -->
        <div class="stack" style="display: flex; flex-direction: column; gap: 14px">
          <div class="panel">
            <div class="panel__head">
              <h4>🎛️ {{ $t("admin-followup.quickToggle") }}</h4>
              <span class="tag muted" data-test="quick-count">{{ fInt(toggleRows.length) }}/{{ fInt(quickLimit) }}</span>
            </div>
            <div class="panel__body">
              <div v-if="toggleLoading" class="muted sm" data-test="quick-loading">
                {{ $t("admin-followup.quick.searching") }}
              </div>
              <template v-for="(e, i) in toggleRows">
                <div v-if="i > 0" :key="e.id + '-d'" class="divider"></div>
                <div :key="e.id" class="row between">
                  <span class="u">
                    <span class="avatar">{{ initials(e.fullName) }}</span>
                    {{ e.fullName }}
                    <span class="tag" :class="statusTag(e.status)">{{ statusLabel(e.status) }}</span>
                  </span>
                  <span class="row" style="gap: 8px">
                    <button
                      class="btn ok sm"
                      type="button"
                      :disabled="saving || e.status === 'active'"
                      @click="quickToggle(e, 'active')"
                    >
                      {{ $t("admin-followup.toggle.activate") }}
                    </button>
                    <button
                      class="btn danger sm"
                      type="button"
                      :disabled="saving || e.status === 'suspended'"
                      @click="quickToggle(e, 'suspended')"
                    >
                      {{ $t("admin-followup.toggle.suspend") }}
                    </button>
                  </span>
                </div>
              </template>
              <!-- D1: the shortlist must never read as "this is everyone". The
                   hint says so when there are more than the 10 shown, and says
                   something different when a search matched nobody. -->
              <div v-if="quickHint" class="muted sm mt" data-test="quick-hint">
                {{ $t("admin-followup.quick." + quickHint, { n: fInt(quickLimit) }) }}
              </div>
            </div>
          </div>

          <div class="panel">
            <div class="panel__head"><h4>📊 {{ $t("admin-followup.monthSummary") }}</h4></div>
            <div class="panel__body">
              <div class="kpis k3" style="gap: 8px">
                <div class="kpi" style="padding: 10px">
                  <div class="v mono" style="font-size: 18px">{{ fInt(summary.leavers) }}</div>
                  <div class="l">{{ $t("admin-followup.kpi.leavers") }}</div>
                </div>
                <div class="kpi" style="padding: 10px">
                  <div class="v mono" style="font-size: 18px">{{ fInt(summary.suspensions) }}</div>
                  <div class="l">{{ $t("admin-followup.kpi.suspensions") }}</div>
                </div>
                <div class="kpi" style="padding: 10px">
                  <div class="v mono" style="font-size: 18px">{{ fInt(summary.edits) }}</div>
                  <div class="l">{{ $t("admin-followup.kpi.edits") }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 4) HR-action audit log -->
      <div class="panel mt">
        <div class="panel__head">
          <h4>📜 {{ $t("admin-followup.logTitle") }}</h4>
          <span class="tag muted" style="margin-inline-start: auto">
            {{ $t("admin-followup.recentBadge", { n: log.length }) }}
          </span>
        </div>
        <div class="panel__body p0">
          <div v-if="log.length" class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>{{ $t("admin-followup.cols.employee") }}</th>
                  <th>{{ $t("admin-followup.cols.action") }}</th>
                  <th>{{ $t("admin-followup.cols.date") }}</th>
                  <th>{{ $t("admin-followup.cols.by") }}</th>
                  <th>{{ $t("admin-followup.cols.note") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in log" :key="a.id">
                  <td>
                    <div class="u">
                      <span class="avatar">{{ initials(a.employeeName) }}</span>
                      {{ a.employeeName }}
                    </div>
                  </td>
                  <td><span class="tag" :class="actionTag(a.action)">{{ actionLabel(a.action) }}</span></td>
                  <td>{{ fDate(a.date) }}</td>
                  <td>{{ a.by }}</td>
                  <td class="muted">{{ a.note || "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="state">{{ $t("admin-followup.empty") }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPost, apiPatch } from "../../api/vivo/http";
import {
  actionTag,
  statusTag,
  normalizeAction,
  monthSummary,
  initials,
  QUICK_TOGGLE_LIMIT,
  quickListParams,
  quickListHint,
} from "./admin-followup-vm";

// "edit" removed: it PATCHed /employees/:id with { note }, which §4 rejects
// (UpdateEmployeeDto has no note field). The three status actions below are real.
const ACTION_KEYS = ["leave", "activate", "suspend"];

export default {
  name: "VivoAdminFollowup",
  data() {
    return {
      employees: [],
      log: [],
      loading: true,
      error: false,
      saving: false,
      successMsg: "",
      errorMsg: "",
      q: "",
      // D1: the quick-toggle list is its own SERVER-side query (10 rows, or the
      // search results), separate from `employees`, which still feeds the
      // Action Center's full select.
      toggleRows: [],
      toggleTotal: 0,
      toggleLoading: false,
      quickLimit: QUICK_TOGGLE_LIMIT,
      searchTimer: null,
      actionKeys: ACTION_KEYS,
      form: {
        employeeId: "",
        action: ACTION_KEYS[0],
        lastWorkDay: "",
        note: "",
      },
    };
  },
  async mounted() {
    try {
      await this.refresh();
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  watch: {
    // The page search box is the quick panel's search (there is deliberately
    // only one search field on this screen).
    q: function () {
      this.onSearch();
    },
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  computed: {
    summary() {
      return monthSummary(this.log);
    },
    dateVisible() {
      return this.form.action === "leave" || this.form.action === "suspend";
    },
    quickHint() {
      return quickListHint({ shown: this.toggleRows.length, total: this.toggleTotal, q: this.q });
    },
  },
  methods: {
    async refresh() {
      const [emps, actions] = await Promise.all([
        // `employees` still backs the Action Center select, which needs the
        // options; only the quick-toggle shortlist is capped at 10.
        apiGet("/employees", { limit: 100 }),
        apiGet("/hr-actions", { limit: 100 }),
        this.loadToggleList(),
      ]);
      this.employees = (emps && emps.data) || [];
      this.log = (actions && actions.data) || [];
    },
    // D1: server-side, so searching reaches employees beyond the first page —
    // the old client-side filter could only ever see the 100 already loaded.
    async loadToggleList() {
      this.toggleLoading = true;
      try {
        const res = await apiGet("/employees", quickListParams(this.q));
        this.toggleRows = (res && res.data) || [];
        this.toggleTotal = (res && res.meta && res.meta.total) || this.toggleRows.length;
      } catch (e) {
        this.toggleRows = [];
        this.toggleTotal = 0;
      } finally {
        this.toggleLoading = false;
      }
    },
    onSearch() {
      // Debounced: every keystroke firing a request would also trip the API's
      // rate limiting on a fast typist.
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.loadToggleList();
      }, 300);
    },
    async submitAction() {
      this.errorMsg = "";
      this.successMsg = "";
      if (!this.form.employeeId) {
        this.errorMsg = this.$t("admin-followup.form.employeeRequired");
        return;
      }
      if (this.dateVisible && !this.form.lastWorkDay) {
        this.errorMsg = this.$t("admin-followup.form.dateRequired");
        return;
      }
      this.saving = true;
      try {
        const id = this.form.employeeId;
        const note = this.form.note || undefined;
        // lastWorkDay is required + captured for "leave"/"suspend" (dateVisible)
        // but was never sent — the backend silently stamped leftAt=now() instead
        // of the HR-chosen date. Send it as `effectiveDate` now that §4 accepts it.
        const effectiveDate = this.dateVisible && this.form.lastWorkDay ? this.form.lastWorkDay : undefined;
        if (this.form.action === "activate") {
          await apiPatch("/employees/" + id + "/status", { status: "active", note });
        } else if (this.form.action === "suspend") {
          await apiPatch("/employees/" + id + "/status", { status: "suspended", note, effectiveDate });
        } else if (this.form.action === "leave") {
          await apiPost("/employees/" + id + "/leave", { note, effectiveDate });
        }
        // No catch-all: the old "edit" action PATCHed /employees/:id with {note},
        // which §4 UpdateEmployeeDto rejects (no `note` field → 400). "edit" is
        // removed from ACTION_KEYS; there is no employee-note endpoint in §4.
        await this.refresh();
        this.successMsg = this.$t("admin-followup.form.success");
        this.resetForm();
      } catch (e) {
        const body = e && e.response && e.response.data;
        this.errorMsg = (body && body.message) || this.$t("admin-followup.error");
      } finally {
        this.saving = false;
      }
    },
    async quickToggle(emp, status) {
      this.errorMsg = "";
      this.successMsg = "";
      this.saving = true;
      try {
        await apiPatch("/employees/" + emp.id + "/status", { status });
        await this.refresh();
        this.successMsg = this.$t("admin-followup.form.statusChanged", { name: emp.fullName });
      } catch (e) {
        const body = e && e.response && e.response.data;
        this.errorMsg = (body && body.message) || this.$t("admin-followup.error");
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      this.form = {
        employeeId: "",
        action: ACTION_KEYS[0],
        lastWorkDay: "",
        note: "",
      };
    },
    actionTag(action) {
      return actionTag(action);
    },
    actionLabel(action) {
      return this.$t("admin-followup.actionLog." + normalizeAction(action));
    },
    statusTag(status) {
      return statusTag(status);
    },
    statusLabel(status) {
      return this.$t("admin-followup.statuses." + status);
    },
    initials(name) {
      return initials(name);
    },
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
  },
};
</script>
